import { loginWithWechatCode, getUserInfo } from '@/api/auth'
import { getToken, setToken, setUserInfo, removeToken, removeUserInfo } from '@/utils/auth'

const WECHAT_APP_ID = process.env.VUE_APP_WECHAT_APP_ID || 'wx4d4d3af3f428d65d'
export const WECHAT_BOUND_KEY = 'deepark_wechat_bound'
const OAUTH_CODE_KEY = 'deepark_wx_oauth_code'
const REDEMPTION_DIST_ID_KEY = 'deepark_redemption_distribution_id'
const REDEMPTION_DISTRIBUTOR_ID_KEY = 'deepark_redemption_distributor_id'
const TRANSFER_TOKEN_KEY = 'deepark_transfer_gift_token'
const OAUTH_REDIRECT_TS_KEY = 'deepark_wx_oauth_redirect_ts'

function syncWechatBoundFlag(userInfo) {
  const user = userInfo?.user || userInfo
  const code = user?.wechatCode || user?.wechat_code
  if (code && String(code).trim()) {
    localStorage.setItem(WECHAT_BOUND_KEY, '1')
  } else {
    localStorage.removeItem(WECHAT_BOUND_KEY)
  }
}

function isPrivateLanHost(hostname) {
  const h = hostname || (typeof window !== 'undefined' ? window.location.hostname : '')
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    /^192\.168\.\d{1,3}\.\d{1,3}$/.test(h) ||
    /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h) ||
    /^172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}$/.test(h)
  )
}

/**
 * 微信网页 OAuth 仅支持已在公众号配置的备案域名，局域网 IP / localhost 不可用。
 * 测试环境（192.168.x.x）应跳过自动 OAuth，改用手动手机号登录。
 */
export function isWechatOAuthSupported() {
  if (process.env.VUE_APP_DISABLE_WECHAT_AUTO_OAUTH === 'true') {
    return false
  }
  if (typeof window === 'undefined') {
    return false
  }
  return !isPrivateLanHost(window.location.hostname)
}

export function isWechatBrowser() {
  return /micromessenger/i.test(navigator.userAgent || '')
}

export function getUrlCode(name) {
  const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i')
  const r = window.location.search.substr(1).match(reg)
  if (r != null) return decodeURIComponent(r[2])
  return null
}

export function getUrlState() {
  return getUrlCode('state')
}

export function cleanWechatCodeFromUrl() {
  try {
    const url = new URL(window.location.href)
    url.searchParams.delete('code')
    url.searchParams.delete('state')
    const qs = url.searchParams.toString()
    const next = url.pathname + (qs ? '?' + qs : '') + (url.hash || '')
    window.history.replaceState({}, '', next)
  } catch (e) {
    /* ignore */
  }
}

/** Persist coupon page query params before OAuth (params may be lost on WeChat callback). */
export function persistCouponPageContext(href = window.location.href) {
  try {
    const fromUrl = new URL(href)
    const distributionId = fromUrl.searchParams.get('distributionId')
    const distributorId = fromUrl.searchParams.get('distributorId')
    const token = fromUrl.searchParams.get('token')
    if (distributionId) {
      sessionStorage.setItem(REDEMPTION_DIST_ID_KEY, distributionId)
    }
    if (distributorId) {
      sessionStorage.setItem(REDEMPTION_DISTRIBUTOR_ID_KEY, distributorId)
    }
    if (token) {
      sessionStorage.setItem(TRANSFER_TOKEN_KEY, token)
    }
  } catch (e) {
    /* ignore */
  }
}

export function restoreCouponPageContext() {
  return {
    distributionId: sessionStorage.getItem(REDEMPTION_DIST_ID_KEY),
    distributorId: sessionStorage.getItem(REDEMPTION_DISTRIBUTOR_ID_KEY),
    token: sessionStorage.getItem(TRANSFER_TOKEN_KEY)
  }
}

function shouldAutoRedirectOAuth() {
  try {
    const last = Number(sessionStorage.getItem(OAUTH_REDIRECT_TS_KEY) || 0)
    return Date.now() - last > 60000
  } catch (e) {
    return true
  }
}

function resolveOAuthState(explicitState, redirectUrl = '') {
  if (explicitState) return explicitState
  const path = redirectUrl || window.location.pathname || ''
  if (path.includes('/transfer')) return 'transfer'
  if (path.includes('/redemption')) return 'redemption'
  if (path.includes('/login')) return 'login'
  return 'auth'
}

/**
 * OAuth callback: exchange WeChat code for token when URL contains code.
 */
export async function tryWeChatAutoLogin(options = {}) {
  const result = await tryWeChatAutoLoginDetailed(options)
  return result.ok
}

export async function tryWeChatAutoLoginDetailed(options = {}) {
  if (!isWechatBrowser()) {
    return { ok: false, reason: 'not_wechat' }
  }
  if (getToken()) {
    return { ok: true }
  }

  const code = getUrlCode('code')
  if (!code) {
    return { ok: false, reason: 'no_code' }
  }
  if (sessionStorage.getItem(OAUTH_CODE_KEY) === code) {
    cleanWechatCodeFromUrl()
    return { ok: false, reason: 'code_used' }
  }
  sessionStorage.setItem(OAUTH_CODE_KEY, code)
  try {
    const response = await loginWithWechatCode(code, { silent: options.silent !== false })
    const accessToken = response.data?.access_token
    if (!accessToken) {
      throw new Error(response.msg || 'WeChat login failed')
    }
    setToken(accessToken)
    sessionStorage.removeItem(OAUTH_CODE_KEY)
    sessionStorage.removeItem(OAUTH_REDIRECT_TS_KEY)
    cleanWechatCodeFromUrl()
    try {
      const userInfo = await getUserInfo()
      setUserInfo(userInfo)
      syncWechatBoundFlag(userInfo)
    } catch (e) {
      console.warn('Failed to load user info after WeChat login', e)
    }
    return { ok: true }
  } catch (e) {
    sessionStorage.removeItem(OAUTH_CODE_KEY)
    cleanWechatCodeFromUrl()
    console.warn('WeChat OAuth login failed', e)
    return {
      ok: false,
      reason: 'login_failed',
      message: e?.message || '微信登录失败'
    }
  }
}

/**
 * WeChat in-app login flow: exchange code if present, otherwise redirect to OAuth.
 * @returns {{ status: 'logged_in'|'oauth_redirect'|'not_wechat'|'need_oauth'|'need_login'|'failed', message?: string }}
 */
export async function ensureWeChatLogin(options = {}) {
  const { autoRedirect = false, autoRedirectOnlyIfBound = false, redirectUrl } = options
  if (!isWechatBrowser()) {
    return { status: 'not_wechat' }
  }
  if (getToken()) {
    return { status: 'logged_in' }
  }

  persistCouponPageContext(redirectUrl || window.location.href)

  const code = getUrlCode('code')
  if (code) {
    const result = await tryWeChatAutoLoginDetailed()
    if (result.ok) {
      return { status: 'logged_in' }
    }
    return { status: 'failed', message: result.message }
  }

  const bound = localStorage.getItem(WECHAT_BOUND_KEY) === '1'
  const shouldRedirect = autoRedirect && shouldAutoRedirectOAuth() && isWechatOAuthSupported()
  if (shouldRedirect && (!autoRedirectOnlyIfBound || bound)) {
    redirectWeChatOAuth({ redirectUrl, state: options.state })
    return { status: 'oauth_redirect' }
  }
  return { status: bound ? 'need_oauth' : 'need_login' }
}

/**
 * Token 失效时静默恢复会话：清除过期凭证后尝试微信 OAuth 自动登录。
 * @returns {Promise<{ status: string, message?: string }>}
 */
export async function recoverSessionOnUnauthorized(options = {}) {
  removeToken()
  removeUserInfo()
  return ensureWeChatLogin({
    autoRedirect: true,
    autoRedirectOnlyIfBound: true,
    redirectUrl: options.redirectUrl || window.location.href,
    state: options.state || 'auth'
  })
}

/**
 * 路由守卫：微信内且曾绑定微信时，无 token 自动 OAuth
 */
export async function handleWeChatGuardAuth(targetFullPath) {
  if (!isWechatBrowser()) {
    return 'not_wechat'
  }
  if (getToken()) {
    return 'logged_in'
  }

  const code = getUrlCode('code')
  if (code) {
    const result = await tryWeChatAutoLoginDetailed()
    return result.ok ? 'logged_in' : 'failed'
  }

  const bound = localStorage.getItem(WECHAT_BOUND_KEY) === '1'
  if (bound && shouldAutoRedirectOAuth() && isWechatOAuthSupported()) {
    const redirectUrl = window.location.origin + targetFullPath
    redirectWeChatOAuth({ redirectUrl, state: 'auth' })
    return 'oauth_redirect'
  }

  return 'need_login'
}

/** Redirect to WeChat OAuth (snsapi_userinfo); callback returns to redirectUrl with code. */
export function redirectWeChatOAuth(options = {}) {
  if (!isWechatOAuthSupported()) {
    console.warn('[WeChat OAuth] 当前为局域网/IP 测试地址，无法使用微信 OAuth，请改用手机号登录')
    return false
  }
  const redirectUrl = options.redirectUrl || window.location.href.split('#')[0]
  persistCouponPageContext(redirectUrl)
  try {
    sessionStorage.setItem(OAUTH_REDIRECT_TS_KEY, String(Date.now()))
  } catch (e) {
    /* ignore */
  }

  const state = resolveOAuthState(options.state, redirectUrl)
  const redirectUri = encodeURIComponent(redirectUrl)
  window.location.href =
    'https://open.weixin.qq.com/connect/oauth2/authorize?appid=' +
    WECHAT_APP_ID +
    '&redirect_uri=' +
    redirectUri +
    '&response_type=code&scope=snsapi_userinfo&state=' +
    state +
    '#wechat_redirect'
  return true
}

export { WECHAT_APP_ID }
